import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

/**
 * Thin indeterminate bar along the viewport top while a navigation is
 * resolving (lazy chunks, guards, resolvers). Only appears when a navigation
 * takes longer than the delay, so fast transitions never flash it.
 */
@Component({
  selector: 'bd-nav-progress',
  template: `
    @if (visible()) {
      <div
        class="bar"
        role="progressbar"
        i18n-aria-label="@@nav.loading"
        aria-label="A carregar a página"
      ></div>
    }
  `,
  styles: `
    :host {
      position: fixed;
      inset-block-start: 0;
      inset-inline: 0;
      block-size: 3px;
      z-index: 1000;
      pointer-events: none;
    }

    .bar {
      block-size: 100%;
      inline-size: 40%;
      background: var(--brand);
      border-radius: 2px;
      animation: bd-nav-progress-sweep 1.1s ease-in-out infinite;
    }

    @keyframes bd-nav-progress-sweep {
      from {
        transform: translateX(-100%);
      }
      to {
        transform: translateX(350%);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .bar {
        animation: none;
        inline-size: 100%;
        opacity: 0.6;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavProgressComponent {
  static readonly SHOW_DELAY_MS = 250;

  protected readonly visible = signal(false);
  #pending: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    const router = inject(Router);
    inject(DestroyRef).onDestroy(() => clearTimeout(this.#pending));
    router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        clearTimeout(this.#pending);
        this.#pending = setTimeout(
          () => this.visible.set(true),
          NavProgressComponent.SHOW_DELAY_MS,
        );
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        clearTimeout(this.#pending);
        this.visible.set(false);
      }
    });
  }
}
